import {
  createAppSelector,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectSectionUsers,
} from "./base";
import { selectOrgUserByUserIdMap } from "./org";
import { selectPageById } from "./page";
import type { EnabledSectionUser, UserWithSectionUserType } from "./types";

import type { RootState } from "store";

export const selectEnabledSectionUsers = createAppSelector(
  [selectSectionUsers, selectOrgUserByUserIdMap],
  (sectionUsers, orgUsersMap) =>
    sectionUsers.filter((cu) => {
      // If the course user is disabled, then of course it's not enabled.
      if (cu.is_disabled) {
        return false;
      }

      // But we're not done; we also need to check if the associated OrgUser is also not disabled.
      // Because the API only responds in the context of the current Org, we can assume
      // that the entire contents of the Redux store are for the current Org.
      // Therefore, we can look up the OrgUser by user_id, without worrying about org_id.
      const orgUser = orgUsersMap[cu.user_id];
      if (!orgUser) {
        return true; // If there's no OrgUser, we assume the user is not disabled.
      }

      return !orgUser.is_disabled;
    }) as EnabledSectionUser[],
);

// TODO: Rather than duplicating the logic above, perhaps we could extract a helper function
// and use in both places.
export const selectDisabledSectionUsers = createAppSelector(
  [selectSectionUsers, selectOrgUserByUserIdMap],
  (sectionUsers, orgUsersMap) =>
    sectionUsers.filter((cu) => {
      if (cu.is_disabled) {
        return true;
      }

      const orgUser = orgUsersMap[cu.user_id];
      if (!orgUser) {
        return false;
      }

      return orgUser.is_disabled;
    }),
);

export const selectSectionUsersForCurrentUser = createAppSelector(
  [selectSectionUsers, selectCurrentUserId],
  (sectionUsers, userId) => sectionUsers.filter((cu) => cu.user_id === userId),
);

export const selectAllSectionUsersByUserId = createAppSelector(
  [selectSectionUsers, (state, userId: string | null) => userId],
  (sectionUsers, userId) => sectionUsers.filter((cu) => cu.user_id === userId),
);

export const selectSectionRoleByUserId = createAppSelector(
  [selectSectionUsers, (state, userId: string | null) => userId, selectCurrentSectionId],
  (sectionUsers, userId, sectionId) =>
    sectionUsers.find((cu) => cu.user_id === userId && cu.section_id === sectionId)?.role || null,
);

export const selectSectionUserByUserId = createAppSelector(
  [selectSectionUsers, (state, userId: string | null) => userId, selectCurrentSectionId],
  (sectionUsers, userId, sectionId) =>
    sectionUsers.find((cu) => cu.user_id === userId && cu.section_id === sectionId) || null,
);

export const selectSectionUserBySectionId = createAppSelector(
  [selectSectionUsersForCurrentUser, (state, sectionId: string | null) => sectionId],
  (sectionUsers, sectionId) => sectionUsers.find((cu) => cu.section_id === sectionId) || null,
);

export const selectSectionUserIdByUserId = createAppSelector(
  [selectSectionUsers, (state, userId: string | null) => userId, selectCurrentSectionId],
  (sectionUsers, userId, sectionId) =>
    sectionUsers.find((cu) => cu.user_id === userId && cu.section_id === sectionId)?.id || null,
);

export const selectCurrentSectionUser = createAppSelector(
  [selectSectionUsers, selectCurrentSectionId, selectCurrentUserId],
  (sectionUsers, sectionId, userId) =>
    sectionUsers.find((cu) => cu.section_id === sectionId && cu.user_id === userId),
);

/**
 * Identical to `selectCurrentSectionUser`, but returns `undefined` if the section user is disabled.
 */
export const selectCurrentSectionUserIfEnabled = createAppSelector(
  [selectEnabledSectionUsers, selectCurrentSectionId, selectCurrentUserId],
  (enabledSectionUsers, sectionId, userId) =>
    enabledSectionUsers.find((cu) => cu.section_id === sectionId && cu.user_id === userId),
);

export const selectCurrentUserIsEnabledForSection = createAppSelector(
  [selectCurrentSectionUserIfEnabled],
  (enabledSectionUser) => Boolean(enabledSectionUser),
);

export const selectCurrentSectionUserId = createAppSelector(
  [selectCurrentSectionUser],
  (sectionUser) => (sectionUser ? sectionUser.id : null),
);

// In most cases, you should use capabilities (canAuthorSection, canParticipateInSection, etc.) instead of roles
// (student, instructor, etc.) to determine what the user can do. For example, instead of checking if the current
// course role is "student", you should check if the user can participate in the course.
export const selectCurrentSectionRole = createAppSelector(
  [selectCurrentSectionUserIfEnabled],
  (sectionUser) => sectionUser?.role || null,
);

export const selectSectionRoleBySectionId = createAppSelector(
  [selectEnabledSectionUsers, selectCurrentUserId, (state, sectionId: string) => sectionId],
  (sectionUsers, userId, sectionId) =>
    sectionUsers.find((cu) => cu.section_id === sectionId && cu.user_id === userId)?.role || null,
);

export const selectSectionUserById = (state: RootState, sectionUserId: string) =>
  state.section_users[sectionUserId];

export const selectUserWithSectionUserBySectionUserId = createAppSelector(
  [selectSectionUserById, (state) => state.users],
  (sectionUser, users) => {
    if (sectionUser) {
      const user = users[sectionUser.user_id];
      if (user) {
        return {
          section_user_id: sectionUser.id,
          ...sectionUser,
          ...user,
        } as UserWithSectionUserType;
      }
    }
  },
);

export const selectEnabledSectionUsersForCurrentSection = createAppSelector(
  [selectEnabledSectionUsers, selectCurrentSectionId],
  (enabledSectionUsers, sectionId) =>
    enabledSectionUsers.filter((cu) => cu.section_id === sectionId),
);

export const selectEnabledStudentsForCurrentSection = createAppSelector(
  [selectEnabledSectionUsersForCurrentSection],
  (enabledSectionUsers) => enabledSectionUsers.filter((cu) => cu.role === "student"),
);

export const selectEnabledSectionUsersBySectionId = createAppSelector(
  [(state, sectionId: string) => sectionId, selectEnabledSectionUsers],
  (sectionId, enabledSectionUsers) =>
    enabledSectionUsers.filter((cu) => cu.section_id === sectionId),
);

export const selectEnabledSectionUsersByPageId = createAppSelector(
  [selectPageById, selectEnabledSectionUsers],
  (page, enabledSectionUsers) =>
    enabledSectionUsers.filter((cu) => cu.section_id === page?.section_id),
);
